// apps/roleforge-app/src/pages/AuditConversePage.tsx
// Plan 3e-i — Phase 2.4: AuditConversePage hosts the persistent shell.
// Layout rework: page owns the activeAssetId state so the right column can
// open assets in the middle column.
//
// One /converse SSE stream multiplexes events across surfaces (chat,
// operating model, activity). The page owns the SSE router and the
// chat transcript state; the panels subscribe themselves through props.

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { AuditExperienceContext } from '../components/audit/AuditExperienceShell';
import { AuditPersistentShell } from '../components/audit/AuditPersistentShell';
import type { ActivationMomentLayoutProps } from '../components/audit/ActivationMomentLayout';
import { composeActivationData } from '../components/audit/composeActivationData';
import type { AssetId } from '../components/audit/AssetsColumn';
import { CapExceededModal } from '../components/cap/CapExceededModal';
import { AabSoftRedirectModal } from '../components/vertical/AabSoftRedirectModal';
import { CreditDisclosureNotice } from '../components/chat/CreditDisclosureNotice';
import { HostedCreditApprovalNotice } from '../components/chat/HostedCreditApprovalNotice';
import { UnsentMessageNotice } from '../components/chat/UnsentMessageNotice';
import { SHOW_CUSTOMER_CREDIT_DISCLOSURE } from '../lib/credit-disclosure';
import type { CreditActionDisclosure } from '@vibecontrol/shared';
import {
  getAudit,
  approveChangeProposal,
  listChangeProposals,
  type AuditRow,
  type ChangeProposal,
  type Message,
  type Opportunity,
  type OpsCanvasEngineRow,
  type RoleforgeAgentProfile,
} from '../lib/api-client';
import { fetchTeam } from '../lib/materialize-api';
import { streamConverse } from '../lib/converse-stream';
import { parseCapError, type CapError } from '../lib/cap-error';
import { createSseEventRouter } from '../lib/sse-event-router';
import { useAuth } from '../lib/auth';
import type { ConverseEvent } from '../lib/converse-types';
import { parseCustomerCreditDisclosure } from '../lib/credit-disclosure';
import {
  createRoleforgeActionKey,
  loadPendingRoleforgeApproval,
  isTerminalRoleforgeApprovalEvent,
  pendingRoleforgeApprovalFromEvent,
  savePendingRoleforgeApproval,
  type PendingRoleforgeApproval,
} from '../lib/hosted-credit-approval';
import {
  appendUnsentMessage,
  createUnsentMessage,
  loadUnsentMessages,
  removeUnsentMessage,
  saveUnsentMessages,
  type UnsentMessage,
} from '../lib/unsent-message';

const API_BASE = import.meta.env.VITE_VIBECONTROL_API_URL ?? 'https://api.vibecontrol.app';

function composerPlaceholder(status: string, sending: boolean): string {
  switch (status) {
    case 'ready_to_materialize':
      return 'Discovery is complete — proceed to the next step.';
    case 'materializing':
      return 'Your AI team is still being set up…';
    case 'materialized':
      return sending
        ? 'RoleForge is reviewing the proposed change…'
        : 'Ask about changes to your live AI team…';
    case 'failed':
      return 'Setup needs attention before chat can continue.';
    case 'synthesizing':
      return sending
        ? 'The agent is synthesising your answers…'
        : 'Type Continue to finish synthesis, or add anything important first…';
    default:
      return 'Tell me about your business…';
  }
}

type ConverseErrorEvent = Extract<ConverseEvent, { type: 'error' }>;

/**
 * Issue #1326 (1) — copy for the pinned undelivered message. Deliberately
 * distinct from `formatConverseTerminalError`, which already explains the
 * cause in the transcript: this card's job is to say the words are still here
 * and nothing has to be retyped.
 */
export const UNSENT_REASON_NOT_DELIVERED =
  'This never reached us, so it was not added to your interview. It is kept in this browser tab until you send it again or discard it.';

/**
 * Reason recorded for the durable copy written the moment a send starts, so
 * the text is never held only in a closure (#1326 (1), review M1). Entries
 * carrying it are not rendered — `finally` either removes the entry or
 * rewrites its reason once the outcome is known.
 */
export const UNSENT_REASON_IN_FLIGHT = '__in_flight__';

/**
 * Error codes that can only be produced BEFORE the server persists the owner
 * message. `converse()` writes the message at step 2, ahead of inference, so
 * `inference_failed` and tool-dispatch errors are all post-persist and must
 * NOT be pinned — offering "Send again" for those would duplicate a stored
 * turn in a paid transcript.
 *
 * Only consulted when the canonical refetch could not run; normally the
 * refetch answers the question directly.
 */
export const PRE_PERSIST_ERROR_CODES = new Set([
  'network_error',
  'http_error',
  'stream_interrupted',
  'message_not_saved',
]);

/**
 * Customer-facing copy for a terminal stream error.
 *
 * Issue #1229 — the buyer of a Blueprint is a non-technical owner-operator.
 * These strings used to leak internal vocabulary ("the runtime", "BYOK",
 * "provider key settings"), which reads as someone else's error message and
 * gives the reader nothing they can act on. Every branch now says, in plain
 * business language, what happened, that their work is safe, and what to do.
 */
export function formatConverseTerminalError(ev: ConverseErrorEvent): string {
  const code = ev.code.toLowerCase();
  const message = ev.message.toLowerCase();
  const combined = `${code} ${message}`;

  // Issue #1200 — a mid-stream drop now arrives as a normal terminal event
  // instead of throwing out of the generator, so it needs its own copy.
  if (combined.includes('stream_interrupted')) {
    return 'The connection dropped while that answer was coming through. Nothing you have shared is lost — send your message again and we will pick up where we left off.';
  }

  if (combined.includes('runtime_offline') || combined.includes('runtime offline')) {
    return 'The computer set up to do this work is offline right now, so nothing could run. Nothing you have shared is lost — bring it back online, then send your message again.';
  }

  if (combined.includes('runtime_incompatible')) {
    return 'The computer set up to do this work needs the latest VibeControl update before it can continue. Nothing you have shared is lost — update it, then try again.';
  }

  if (combined.includes('runtime_timeout')) {
    return 'The computer set up to do this work did not finish in time. Nothing you have shared is lost — wait a moment, then try again.';
  }

  if (combined.includes('runtime_disconnected')) {
    return 'The computer set up to do this work disconnected before the answer finished. Nothing you have shared is lost — reconnect it, then try again.';
  }

  if (combined.includes('runtime_continuation_unavailable')) {
    return 'The computer set up to do this work could not finish saving an audit update. Nothing you have shared is lost — update or reconnect it, then try again.';
  }

  if (combined.includes('runtime_cancelled')) {
    return 'That answer was cancelled before it finished. Nothing you have shared is lost — try again when you are ready.';
  }

  if (
    combined.includes('byok') ||
    combined.includes('api key') ||
    combined.includes('provider key') ||
    combined.includes('credential')
  ) {
    return 'The AI service connected to your account would not accept this request. Check your AI connection settings, then try again.';
  }

  if (
    combined.includes('provider') ||
    combined.includes('rate limit') ||
    combined.includes('quota')
  ) {
    return 'The AI service is busy or has reached its limit right now. Wait a moment, then try again.';
  }

  return 'We ran into a problem and reported it automatically. Please try again later.';
}

/**
 * Try to read the AuditExperienceContext from the surrounding outlet. If the
 * page is rendered outside an outlet (e.g., a parent component passes
 * `audit` and `refreshAudit` directly via props), `useOutletContext()` may
 * return undefined; callers must handle that.
 */
function useOutletContextSafe<T>(): T | undefined {
  try {
    return useOutletContext<T>();
  } catch {
    return undefined;
  }
}

interface Props {
  audit?: AuditRow;
  refreshAudit?: () => Promise<void>;
}

export function AuditConversePage(props: Props = {}) {
  const ctx = useOutletContextSafe<AuditExperienceContext>();
  const audit = props.audit ?? ctx?.audit;
  const refreshAudit = props.refreshAudit ?? ctx?.refreshAudit ?? (async () => {});

  const { jwt } = useAuth();

  const [messages, setMessages] = useState<Message[]>([]);
  const [agentProfile, setAgentProfile] = useState<RoleforgeAgentProfile | null>(null);
  const [hydratedAudit, setHydratedAudit] = useState<AuditRow | null>(null);
  const [opsCanvas, setOpsCanvas] = useState<OpsCanvasEngineRow[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [capError, setCapError] = useState<CapError | null>(null);
  const [showAabRedirect, setShowAabRedirect] = useState(false);
  const [sending, setSending] = useState(false);
  const [activationData, setActivationData] = useState<ActivationMomentLayoutProps | undefined>(
    undefined,
  );
  const [activeAssetId, setActiveAssetId] = useState<AssetId | null>(null);
  const [changeProposals, setChangeProposals] = useState<ChangeProposal[]>([]);
  const [pendingHostedApproval, setPendingHostedApproval] = useState<PendingRoleforgeApproval | null>(null);
  const [creditDisclosure, setCreditDisclosure] = useState<CreditActionDisclosure | null>(null);
  // Issue #1326 (1) — messages the server never received. A LIST, not a slot:
  // two failures in a row (the normal shape of being offline) used to have the
  // second destroy the first.
  const [unsentMessages, setUnsentMessages] = useState<UnsentMessage[]>([]);

  // One router per mounted audit. Surfaces (operating-model panel, agent
  // activity panel, chat) all subscribe to it; we dispatch every SSE frame.
  const router = useMemo(() => createSseEventRouter(), [audit?.id]);

  // Load initial messages on mount / when audit changes.
  //
  // Issue #1228 — `GET /audits/:id` carries the full unpaginated transcript.
  // AuditExperienceShell has already fetched it to resolve the audit row, so
  // when its payload matches this audit we hydrate from that response instead
  // of downloading every message a second time. The fetch still runs when the
  // page is mounted outside the shell (props path, tests, deep links).
  useEffect(() => {
    if (!jwt || !audit) return;

    const preloaded = ctx?.auditDetail;
    if (preloaded && preloaded.audit.id === audit.id) {
      setHydratedAudit(preloaded.audit);
      setMessages(preloaded.messages);
      setAgentProfile(preloaded.agent ?? null);
      setOpsCanvas(preloaded.ops_canvas);
      setOpportunities(preloaded.opportunities);
      return;
    }

    setHydratedAudit(null);
    void getAudit(jwt, audit.id).then((res) => {
      setHydratedAudit(res.audit);
      setMessages(res.messages);
      setAgentProfile(res.agent ?? null);
      setOpsCanvas(res.ops_canvas);
      setOpportunities(res.opportunities);
    });
  }, [jwt, audit?.id, ctx?.auditDetail]);

  useEffect(() => {
    setPendingHostedApproval(audit ? loadPendingRoleforgeApproval(audit.id) : null);
    setUnsentMessages(audit ? loadUnsentMessages(audit.id) : []);
  }, [audit?.id]);

  /**
   * #1326 (1), review M1 — durable copy written at send start. Marked
   * in-flight so it is not rendered while the send is still running.
   */
  const markUnsentInFlight = useCallback(
    (text: string) => {
      if (!audit) return;
      setUnsentMessages((prev) => {
        const next = appendUnsentMessage(
          prev,
          createUnsentMessage(text, UNSENT_REASON_IN_FLIGHT),
        );
        saveUnsentMessages(audit.id, next);
        return next;
      });
    },
    [audit?.id],
  );

  const refreshChangeProposals = useCallback(async () => {
    if (!jwt || !audit || audit.status !== 'materialized') {
      setChangeProposals([]);
      return;
    }
    try {
      const res = await listChangeProposals(jwt, audit.id);
      setChangeProposals(res.proposals);
    } catch {
      setChangeProposals([]);
    }
  }, [jwt, audit?.id, audit?.status]);

  useEffect(() => {
    void refreshChangeProposals();
  }, [refreshChangeProposals]);

  // Lazy-load activation data the first time we transition to
  // ready_to_materialize. Guarded on `activationData === undefined` so the
  // fetch fires exactly once per status transition.
  useEffect(() => {
    if (!jwt || !audit) return;
    if (audit.status !== 'ready_to_materialize') return;
    if (activationData !== undefined) return;

    let cancelled = false;
    void Promise.all([getAudit(jwt, audit.id), fetchTeam(audit.id, jwt)])
      .then(([auditRes, teamRes]) => {
        if (cancelled) return;
        const composed = composeActivationData({
          audit: auditRes.audit,
          teamRecs: teamRes.recommendations,
          opportunities: auditRes.opportunities,
          recommendations: auditRes.recommendations,
          roiInputs: auditRes.roi_inputs,
          opsCanvas: auditRes.ops_canvas,
        });
        setActivationData(composed);
      })
      .catch(() => {
        // Soft-fail: the shell's loading placeholder stays visible.
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, audit?.id, audit?.status, activationData]);

  const handleSend = useCallback(
    async (text: string, continuation?: PendingRoleforgeApproval) => {
      if (!jwt || !audit || sending) return;
      setSending(true);
      setCreditDisclosure(null);
      const actionKey = continuation?.actionKey ?? createRoleforgeActionKey();

      // Optimistic user message (no id yet — use a temporary key)
      const optimisticMsg: Message = {
        id: `optimistic-${Date.now()}`,
        audit_id: audit.id,
        role: 'user',
        content: text,
        tool_call_id: null,
        tool_name: null,
        metadata: {},
        position: messages.length,
        created_at: new Date().toISOString(),
      };
      if (!continuation) setMessages((prev) => [...prev, optimisticMsg]);

      let accumulated = '';
      let terminalErrorMessage: Message | null = null;
      let nextPending: PendingRoleforgeApproval | null = null;
      let sawError = false;
      let streamCompleted = false;
      // Issue #1326 (2) — the activity spinner only cleared on a `done` /
      // `stream_end` / `error` frame reaching the router. A stream that simply
      // stops (server truncates the body, caller aborts, generator returns
      // without a terminal frame) left an "activity.started" row spinning
      // forever next to a re-enabled composer, so the owner could not tell a
      // thinking agent from a dead socket. Tracked here and force-terminated
      // in `finally` regardless of how the stream ended.
      let sawTerminalFrame = false;
      // Issue #1326 (1) — true when the credit-approval path retained the text
      // itself, so the pin must not also claim it.
      let pendingApprovalRetained = false;
      // Set when a terminal error frame carried a code that can only occur
      // BEFORE the server persisted the message. Used only as the fallback
      // when the canonical refetch in `finally` could not run.
      let pinnableErrorCode = false;

      // Issue #1326 (1), review M1 — write the durable copy NOW, not in
      // `finally`. `ChatComposer` clears the textarea synchronously on send,
      // so between that moment and the end of the stream the text existed
      // ONLY in this closure: a tab crash, a navigation, or a laptop lid mid
      // request lost it outright, and "it survives a reload" was true only
      // after a failure had already been observed.
      //
      // The entry is marked in-flight so nothing renders while the send is
      // still in progress; `finally` either drops it or reveals it.
      markUnsentInFlight(text);

      try {
        for await (const ev of streamConverse(API_BASE, jwt, audit.id, text, {
          actionKey,
          ...(continuation ? { resumeToken: continuation.resumeToken } : {}),
        })) {
          // Multiplex every event through the router so the operating-model
          // and activity surfaces can react in real time.
          router.dispatch(ev);

          if (ev.type === 'done' || ev.type === 'stream_end' || ev.type === 'error') {
            sawTerminalFrame = true;
          }

          if (ev.type === 'message_delta') {
            accumulated += ev.text;
            setStreamingText(accumulated);
          } else if (ev.type === 'credit_disclosure') {
            const disclosure = parseCustomerCreditDisclosure(ev.disclosure);
            if (disclosure) setCreditDisclosure(disclosure);
          } else if (ev.type === 'tool_use') {
            if (ev.tool_name === 'suggest_aab_redirect') {
              setShowAabRedirect(true);
            }
            // tool_use frames now surface as inline activity rows in the
            // chat transcript (via the router) — no longer injected into
            // the chat as messages, no longer in a separate panel.
          } else if (ev.type === 'error') {
            sawError = true;
            nextPending = pendingRoleforgeApprovalFromEvent(text, ev);
            if (nextPending) {
              setPendingHostedApproval(nextPending);
              savePendingRoleforgeApproval(audit.id, nextPending);
              continue;
            }
            if (continuation && !isTerminalRoleforgeApprovalEvent(ev)) {
              setPendingHostedApproval(continuation);
              savePendingRoleforgeApproval(audit.id, continuation);
              pendingApprovalRetained = true;
            } else {
              setPendingHostedApproval(null);
              savePendingRoleforgeApproval(audit.id, null);
              if (PRE_PERSIST_ERROR_CODES.has(ev.code.toLowerCase())) {
                pinnableErrorCode = true;
              }
            }
            const cap = parseCapError(ev);
            if (cap) {
              setCapError(cap);
            } else {
              // Generic hosted-execution error — keep the copy customer-safe.
              const errMsg: Message = {
                id: `error-${Date.now()}`,
                audit_id: audit.id,
                role: 'agent',
                content: formatConverseTerminalError(ev),
                tool_call_id: null,
                tool_name: null,
                metadata: {},
                position: -1,
                created_at: new Date().toISOString(),
              };
              terminalErrorMessage = errMsg;
              setMessages((prev) => [...prev, errMsg]);
            }
          }
        }
        streamCompleted = true;
      } finally {
        // Issue #1326 (2) — always terminate the activity surface, whatever
        // happened to the stream. `stream_end` is already the router's
        // no-op-safe terminal frame; dispatching a second one when the server
        // sent its own would be harmless, but we only send it when none
        // arrived so the event log stays honest.
        if (!sawTerminalFrame) {
          router.dispatch({ type: 'stream_end' });
        }
        if (continuation && streamCompleted && !sawError) {
          setPendingHostedApproval(null);
          savePendingRoleforgeApproval(audit.id, null);
        }

        // Re-fetch canonical messages from server. This runs BEFORE the pin
        // decision because it is the only direct evidence of whether the
        // server actually took the message.
        let serverHasMessage: boolean | null = null;
        try {
          const res = await getAudit(jwt, audit.id);
          setHydratedAudit(res.audit);
          setAgentProfile(res.agent ?? null);
          setMessages(terminalErrorMessage ? [...res.messages, terminalErrorMessage] : res.messages);
          setOpsCanvas(res.ops_canvas);
          setOpportunities(res.opportunities);
          serverHasMessage = res.messages.some(
            (m) => m.role === 'user' && m.content === text,
          );
          if (res.audit.status === 'materialized') {
            await refreshChangeProposals();
          }
        } catch {
          // keep optimistic state on fetch failure
        }

        // Issue #1326 (1) — decide what happens to the durable copy written at
        // send start.
        //
        // The first version pinned on EVERY error frame except the credit
        // approval, but `converse()` persists the owner message BEFORE running
        // inference — so `inference_failed` and every tool-dispatch error
        // arrive AFTER the server already has it. Review reproduced the
        // result: the answer rendered twice, the card claimed it "was not
        // added to your interview", and Send-again would have duplicated a
        // stored turn in a paid transcript.
        //
        // The canonical refetch above answers the question directly, for every
        // error code including ones we have not seen yet. The code allow-list
        // is only the fallback for when that refetch itself failed.
        const deliveredCleanly = streamCompleted && !sawError;
        const approvalHoldsTheText = !!nextPending
          || (!!continuation && !!pendingApprovalRetained);
        const lostBeforeServerSawIt = serverHasMessage === null
          ? (!streamCompleted || (sawError && pinnableErrorCode))
          : !serverHasMessage;

        if (approvalHoldsTheText || deliveredCleanly || !lostBeforeServerSawIt) {
          // Nothing was lost: drop ONLY this message's entry. The first version
          // cleared the whole store on any success, so an unrelated later turn
          // deleted a pinned answer the owner had not recovered yet.
          setUnsentMessages((prev) => {
            const next = removeUnsentMessage(prev, text);
            saveUnsentMessages(audit.id, next);
            return next;
          });
        } else {
          setUnsentMessages((prev) => {
            const next = appendUnsentMessage(
              prev,
              createUnsentMessage(text, UNSENT_REASON_NOT_DELIVERED),
            );
            saveUnsentMessages(audit.id, next);
            return next;
          });
        }
        setStreamingText('');
        await refreshAudit();
        setSending(false);
      }
    },
    [jwt, audit, messages.length, refreshAudit, sending, router, refreshChangeProposals],
  );

  const handleApproveProposal = useCallback(
    async (proposalId: string, mode: 'later' | 'now') => {
      if (!jwt || !audit) return;
      await approveChangeProposal(jwt, audit.id, proposalId, mode);
      await refreshChangeProposals();
    },
    [jwt, audit, refreshChangeProposals],
  );

  if (!audit) {
    return null;
  }

  // In-flight entries are the durable copy of a send that is still running —
  // real storage, deliberately not rendered.
  const visibleUnsentMessages = unsentMessages.filter(
    (m) => m.reason !== UNSENT_REASON_IN_FLIGHT,
  );

  const isDisabled =
    sending ||
    audit.status === 'ready_to_materialize' ||
    audit.status === 'materializing' ||
    audit.status === 'failed';
  const placeholder = composerPlaceholder(audit.status, sending);
  const shellAudit = hydratedAudit?.id === audit.id ? hydratedAudit : audit;

  return (
    <>
      <AuditPersistentShell
        audit={shellAudit}
        router={router}
        refreshAudit={refreshAudit}
        messages={messages}
        agentProfile={agentProfile}
        opsCanvas={opsCanvas}
        opportunities={opportunities}
        jwt={jwt}
        streamingAgentText={streamingText}
        onSend={handleSend}
        composerDisabled={isDisabled}
        composerPlaceholder={placeholder}
        activationData={activationData}
        changeProposals={changeProposals}
        onApproveProposal={handleApproveProposal}
        activeAssetId={activeAssetId}
        onSelectAsset={setActiveAssetId}
      />
      {SHOW_CUSTOMER_CREDIT_DISCLOSURE && creditDisclosure && (
        <CreditDisclosureNotice
          disclosure={creditDisclosure}
          className="fixed bottom-5 right-5 z-40 w-[min(22rem,calc(100vw-2rem))]"
        />
      )}
      {pendingHostedApproval && (
        <HostedCreditApprovalNotice
          pending={pendingHostedApproval}
          busy={sending}
          onRetry={() => void handleSend(pendingHostedApproval.message, pendingHostedApproval)}
          onDiscard={() => {
            setPendingHostedApproval(null);
            savePendingRoleforgeApproval(audit.id, null);
          }}
          className="fixed bottom-5 left-1/2 z-50 w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 shadow-xl"
        />
      )}
      {!pendingHostedApproval && visibleUnsentMessages.length > 0 && (
        <div
          data-testid="unsent-message-stack"
          className="fixed bottom-5 left-1/2 z-50 flex w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 flex-col gap-2"
        >
          {visibleUnsentMessages.map((unsent) => (
            <UnsentMessageNotice
              key={unsent.savedAt + unsent.text.slice(0, 32)}
              unsent={unsent}
              busy={sending}
              // Review M2: do NOT delete the durable copy before re-sending.
              // A retry that fails on the way out would then have destroyed
              // the only remaining copy. Success removes it; failure re-pins.
              onRetry={() => void handleSend(unsent.text)}
              onDiscard={() => {
                setUnsentMessages((prev) => {
                  const next = removeUnsentMessage(prev, unsent.text);
                  saveUnsentMessages(audit.id, next);
                  return next;
                });
              }}
              className="shadow-xl"
            />
          ))}
        </div>
      )}
      {capError && (
        <CapExceededModal error={capError} onClose={() => setCapError(null)} />
      )}
      {showAabRedirect && (
        <AabSoftRedirectModal onClose={() => setShowAabRedirect(false)} />
      )}
    </>
  );
}
